import { useUnits } from '@innovyze/stylovyze';
import { Typography } from '@mui/material';
import React from 'react';
import { useGlobalization } from '@Translations';
import { formatUnitValue } from '@Components/RulesList/RulesList.utils';

// I360A-2419/2420 persist/return this shape from the backend as
// `trenchCalculation` on a rehab result row. There is no shared frontend
// type for it yet, so it is declared locally to this component.
export interface TrenchCalculationDefect {
	defectCode: string;
	positionM: number;
	// I360A-2218: continuous defects are shown as a range ("H from 80-88ft")
	// in the design, but the backend does not populate an end position for
	// them yet (only `positionM` is set today for both point and continuous
	// defects). This field is declared so the UI can render a range as soon
	// as the backend starts sending it; until then, continuous defects fall
	// back to the single-position format like point defects.
	endPositionM?: number;
	type: 'point' | 'continuous';
}

export interface TrenchCalculationSegment {
	startM: number;
	endM: number;
	lengthM: number;
	fixType: 'PointFix' | 'SectionalFix';
	defects: TrenchCalculationDefect[];
}

export type TrenchDataStatus =
	| 'calculated'
	| 'noInspectionData'
	| 'noMatchingDefects';

export interface TrenchCalculationBreakdown {
	assetId: string;
	actionId: string;
	nodeId: string;
	calculationMethod: 'pipeLength' | 'trenchLength';
	defectTypesConsidered: string[];
	trenchBufferM: number;
	mergeBufferM: number;
	totalTrenchLengthM: number;
	segments: TrenchCalculationSegment[];
	dataStatus: TrenchDataStatus;
}

interface RehabLengthTooltipContentProps {
	readonly breakdown: TrenchCalculationBreakdown;
}

const NUMERIC_PREFIX_PATTERN = /^[0-9.,-]+/;

// I360A-2218: renders the rich tooltip content shown when hovering a
// calculated Rehab Length value. Previously this data was shown in a
// click-to-open `Drawer` (see git history for `RehabLengthBreakdownDrawer`);
// per design review, it is now a hover tooltip instead.
export default function RehabLengthTooltipContent({
	breakdown,
}: RehabLengthTooltipContentProps) {
	const { t } = useGlobalization();
	const { getSystemValueFormatted } = useUnits();

	// I360A-2218: cap the displayed length at 2 decimal places (rounded, not
	// padded - e.g. 35.6693 -> 35.67, 3.5 stays 3.5).
	const formatLength = (valueM: number): string =>
		formatUnitValue(valueM, 'm', getSystemValueFormatted, 2);

	// I360A-2218: the design shows generated trench segments as a plain
	// numeric range (e.g. "32.5 - 57.5"), unlike the other rows which show
	// the unit suffix (e.g. "12.5 ft"). Reuse the same unit-aware conversion
	// as `formatLength` and strip the trailing unit label rather than
	// duplicating the conversion logic.
	// A single anchored, non-overlapping character class is used (rather
	// than the previous `\s*[^\s0-9.,-]+\s*$` pattern) so the match is a
	// simple linear scan from the start of the string, avoiding the
	// superlinear-backtracking shape flagged by SonarQube rule S5852.
	const formatLengthValueOnly = (valueM: number): string => {
		const formatted = formatLength(valueM);
		const numericPrefix = NUMERIC_PREFIX_PATTERN.exec(formatted);
		return numericPrefix ? numericPrefix[0] : formatted.trim();
	};

	// A continuous defect's range ("H from 80-88 ft") only needs the unit
	// label once, at the end - derive it from a formatted value rather than
	// hardcoding 'ft'/'m'.
	const getLengthUnitSuffix = (valueM: number): string =>
		formatLength(valueM).replace(formatLengthValueOnly(valueM), '').trim();

	// The shared `t()` mock used across this repo's tests does not support
	// i18next-style interpolation, and existing patterns in this codebase
	// (e.g. the old drawer's "{start} {t('to')} {end}") build sentences by
	// concatenating small translated connector words instead - follow that
	// convention here rather than interpolating a template string.
	const formatDefect = (defect: TrenchCalculationDefect): string => {
		if (defect.type === 'continuous' && defect.endPositionM != null) {
			return `${defect.defectCode} ${t('from')} ${formatLengthValueOnly(
				defect.positionM,
			)}-${formatLengthValueOnly(
				defect.endPositionM,
			)} ${getLengthUnitSuffix(defect.endPositionM)}`;
		}
		return `${defect.defectCode} ${t('at')} ${formatLength(
			defect.positionM,
		)}`;
	};

	// Defensive: the backend contract for `trenchCalculation` is not
	// enforced by the type system on the way in (see the `as unknown as`
	// cast in RehabResultTable), so guard against a malformed/partial
	// breakdown rather than throwing inside a DataGrid cell renderer.
	const segments = breakdown.segments ?? [];
	const defects = segments.flatMap(segment => segment.defects ?? []);

	const sectionStyle = { marginTop: '12px' };

	return (
		<div data-cy="rehab-length-tooltip-content">
			<Typography variant="subtitle2">
				{t('Rehab Length Calculation')}
			</Typography>
			<div style={sectionStyle}>
				<Typography variant="body2">
					{t('Defects considered')}:
				</Typography>
				{defects.map((defect, index) => (
					<Typography
						key={`${defect.defectCode}-${index}`}
						variant="caption"
						display="block">
						{formatDefect(defect)}
					</Typography>
				))}
			</div>
			<div style={sectionStyle}>
				<Typography variant="body2">
					{t('Trench buffer')}:{' '}
					{formatLength(breakdown.trenchBufferM)}
				</Typography>
				<Typography variant="body2">
					{t('Merge buffer')}: {formatLength(breakdown.mergeBufferM)}
				</Typography>
			</div>
			<div style={sectionStyle}>
				<Typography variant="body2">
					{t('Generated trench segments')}:
				</Typography>
				{segments.map((segment, index) => (
					<Typography
						key={`${segment.startM}-${segment.endM}-${index}`}
						variant="caption"
						display="block">
						{`${formatLengthValueOnly(segment.startM)} – ${formatLengthValueOnly(segment.endM)}`}
					</Typography>
				))}
			</div>
			<Typography variant="body2" style={sectionStyle}>
				{t('Total trench length')}:{' '}
				{formatLength(breakdown.totalTrenchLengthM)}
			</Typography>
		</div>
	);
}
